import styled from 'styled-components';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CrioButton from '@crio/crio-react-component/dist/cjs/components/Inputs/CrioButton';
import { faCaretDown, faCheck, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import PatientRecordSummary from '../../types/PatientRecordSummary';
import PatientRecordSelect from './PatientRecordSelect';
import TraitsRecordCardType from '../../enums/TraitsRecordCardType';

const StyledCrioButton = styled(CrioButton)(({ theme }) => `
  && {
    flex: 1;
    text-align: center;
    padding: 6px;
    font-size: 14px;
    font-weight: 500;

    &:hover {
      color: #0080C8;
    }

    &:focus {
      outline: none;
    }
  }

  &:not(:first-of-type) {
    border-left: 1px solid ${theme.palette.grey[300]};
  }

  .caret-icon {
    font-size: 16px;
    padding-left: 10px;
  }
`);

const ButtonContainer = styled('div')`
  display: flex;
`;

const RecordSelectContainer = styled('div')(({ theme }) => `
  display: flex;
  gap: 12px;
  padding-top: 8px;
  
  .button {
    min-width: 40px !important;
    background-color: #009CEB !important;
    color: white !important;
    padding: 12px 1px !important;
    
    &:hover {
      background-color: #0080C8 !important;
    }
    
    &:disabled {
      background-color: ${theme.palette.grey[200]} !important;
      color: ${theme.palette.grey[500]} !important;
    }
  }  
  
  .action-icon {
    font-size: 16px;
  }
`);

interface TraitsRecordCardActionsProps {
  traitsRecordCardType: TraitsRecordCardType;
  patientRecordSummaries: PatientRecordSummary[] | undefined;
  recordId: string;
  onAddNewClick: () => void;
  onMapClick: (patientRecordUid : string, isDeleted: boolean) => void;
  onDismissClick: () => void;
}

export default function TraitsRecordCardActions({
  traitsRecordCardType,
  patientRecordSummaries,
  recordId,
  onAddNewClick,
  onMapClick,
  onDismissClick,
}: Readonly<TraitsRecordCardActionsProps>) {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPatientRecord, setSelectedPatientRecord] = useState<PatientRecordSummary>();

  switch (traitsRecordCardType) {
    case TraitsRecordCardType.UPDATE:
      return (
        <>
          <ButtonContainer>
            <StyledCrioButton
              colorTheme="Text"
              data-testid={`add-record-${recordId}-button`}
              onClick={onAddNewClick}
            >
              {t('Traits.Actions.Add Record')}
            </StyledCrioButton>
            <StyledCrioButton
              colorTheme="Text"
              data-testid="update-existing-record-button"
              onClick={() => setShowDropdown((prevShowDropdown) => !prevShowDropdown)}
            >
              {t('Traits.Actions.Update Existing Record')}
              <FontAwesomeIcon icon={faCaretDown} className="caret-icon" />
            </StyledCrioButton>
          </ButtonContainer>
          {showDropdown && (
          <RecordSelectContainer>
            <PatientRecordSelect
              data-testid="patient-record-select"
              patientRecordSummaries={patientRecordSummaries}
              selectedPatientRecord={selectedPatientRecord}
              setSelectedPatientRecord={setSelectedPatientRecord}
            />
            <Button
              className="button"
              data-testid="confirm-mapping-button"
              disabled={!selectedPatientRecord}
              disableRipple
              onClick={() => onMapClick(selectedPatientRecord?.multiRecordUid!, false)}
            >
              <FontAwesomeIcon icon={faCheck} className="action-icon" />
            </Button>
          </RecordSelectContainer>
          )}
        </>
      );

    case TraitsRecordCardType.DELETED:
      return (
        <>
          <ButtonContainer>
            <StyledCrioButton
              colorTheme="Text"
              data-testid={`delete-record-${recordId}-button`}
              onClick={() => setShowDropdown((prevShowDropdown) => !prevShowDropdown)}
            >
              {t('Traits.Actions.Delete Record')}
              <FontAwesomeIcon icon={faCaretDown} className="caret-icon" />
            </StyledCrioButton>
            <StyledCrioButton
              colorTheme="Text"
              data-testid={`dismiss-record-${recordId}-button`}
              onClick={onDismissClick}
            >
              {t('Traits.Actions.Dismiss')}
            </StyledCrioButton>
          </ButtonContainer>
          {showDropdown && (
          <RecordSelectContainer>
            <PatientRecordSelect
              data-testid="patient-record-select"
              patientRecordSummaries={patientRecordSummaries}
              selectedPatientRecord={selectedPatientRecord}
              setSelectedPatientRecord={setSelectedPatientRecord}
            />
            <Button
              className="button"
              data-testid="confirm-delete-button"
              disabled={!selectedPatientRecord}
              disableRipple
              onClick={() => onMapClick(selectedPatientRecord?.multiRecordUid!, true)}
            >
              <FontAwesomeIcon icon={faTrash} className="action-icon" />
            </Button>
          </RecordSelectContainer>
          )}
        </>

      );

    default:
      return null;
  }
}
